import { useEffect, useRef, useState, useCallback } from 'preact/hooks'
import { emailBlastApi } from '@/api/emailBlast'
import type { EmailBlastViewDto } from '@/types/emailBlast'
import { useToast } from './useToast'

interface UseEmailBlastsOptions {
  workspaceId: string
  campaignId: string
  webId?: string
}

interface BlastActionResult {
  success: boolean
  data?: EmailBlastViewDto
}

/**
 * Loads + maintains the list of email blasts for a campaign (optionally one web), and exposes the
 * resume/cancel controls. Action methods patch local state from the returned view and toast outcomes;
 * Resume surfaces the API's 409 (blast not Paused) as a distinct, non-validation error.
 */
export function useEmailBlasts({ workspaceId, campaignId, webId }: UseEmailBlastsOptions) {
  const [blasts, setBlasts] = useState<EmailBlastViewDto[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Per-blast in-flight action ids, so a row's Resume/Cancel buttons can show a spinner / disable.
  const [actioningIds, setActioningIds] = useState<Set<string>>(new Set())

  const { showSuccess, showError, showErrorUnless422 } = useToast()

  // Generation token: only the most recent load applies its result, so a superseded
  // (campaign/web changed) or unmounted load can't write stale data.
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!workspaceId || !campaignId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await emailBlastApi.list(workspaceId, campaignId, webId)
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) setBlasts(res.data)
    setLoading(false)
  }, [workspaceId, campaignId, webId])

  useEffect(() => {
    void load()
    // Invalidate any in-flight load on unmount / scope change.
    return () => { requestIdRef.current++ }
  }, [load])

  const upsert = useCallback((blast: EmailBlastViewDto) => {
    setBlasts(prev => {
      if (!prev) return [blast]
      const idx = prev.findIndex(b => b.Id === blast.Id)
      if (idx === -1) return [blast, ...prev]
      const next = prev.slice()
      next[idx] = blast
      return next
    })
  }, [])

  const setActioning = useCallback((id: string, on: boolean) => {
    setActioningIds(prev => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const resume = useCallback(async (id: string): Promise<BlastActionResult> => {
    setActioning(id, true)
    try {
      const res = await emailBlastApi.resume(workspaceId, id)
      if (res.error || !res.data) {
        // 409 = the blast wasn't Paused (likely already resumed elsewhere) — give a clear message
        // instead of the raw body; everything else falls through to the standard error toast.
        if (res.status === 409) {
          showError('Cannot resume', 'This blast is no longer paused. Refreshing its status.')
        } else {
          showErrorUnless422('Resume failed', res)
        }
        return { success: false }
      }
      upsert(res.data)
      showSuccess('Blast resumed', 'Sending has resumed.')
      return { success: true, data: res.data }
    } finally {
      setActioning(id, false)
    }
  }, [workspaceId, upsert, setActioning, showError, showSuccess, showErrorUnless422])

  const cancel = useCallback(async (id: string): Promise<BlastActionResult> => {
    setActioning(id, true)
    try {
      const res = await emailBlastApi.cancel(workspaceId, id)
      if (res.error || !res.data) {
        showErrorUnless422('Cancel failed', res)
        return { success: false }
      }
      upsert(res.data)
      showSuccess('Blast cancelled', 'No further emails will be sent.')
      return { success: true, data: res.data }
    } finally {
      setActioning(id, false)
    }
  }, [workspaceId, upsert, setActioning, showSuccess, showErrorUnless422])

  return { blasts, loading, error, actioningIds, load, resume, cancel }
}
